import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { ArrowLeft, Table2, Plus, Search, MoreHorizontal, Edit, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TablaDepreciacion = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/catastro/tabla-depreciacion`, { headers });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      item.descripcion?.toLowerCase().includes(s) ||
      String(item.anios_antiguedad).includes(s) ||
      String(item.porcentaje_depreciacion).includes(s)
    );
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/catastro/tabla-depreciacion`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/catastro/tabla-depreciacion/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/api/catastro/tabla-depreciacion/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  return (
    <div className="space-y-6" data-testid="catastro-submodulo">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catastro')} className="hover:bg-slate-100">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
          <Table2 className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tabla Depreciación</h1>
          <p className="text-slate-500">Gestión de tabla depreciación</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredData.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{data.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <Button onClick={() => { setEditingItem(null); setFormData({}); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo">
            <Plus className="w-4 h-4 mr-2" />Nuevo Registro
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rango de Antigüedad</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Desde Años</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Hasta Años</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">% Depreciación</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No hay registros</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.descripcion}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.anios_antiguedad}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.hasta_anios_antiguedad}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.porcentaje_depreciacion}%</td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditingItem(item); setFormData(item); setDialogOpen(true); }}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Tabla Depreciación" : "Nuevo Registro - Tabla Depreciación"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-3">
                <Label>Rango de Antigüedad</Label>
                <Input value={formData.descripcion || ""} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} placeholder="Ej: De 0 a 5 años" data-testid="form-descripcion-dep" />
              </div>
              <div className="space-y-2">
                <Label>Desde Años de Antigüedad</Label>
                <Input type="number" value={formData.anios_antiguedad || ""} onChange={(e) => setFormData({...formData, anios_antiguedad: parseInt(e.target.value) || 0})} placeholder="0" className="text-right" data-testid="form-anios" />
              </div>
              <div className="space-y-2">
                <Label>Hasta Años de Antigüedad</Label>
                <Input type="number" value={formData.hasta_anios_antiguedad || ""} onChange={(e) => setFormData({...formData, hasta_anios_antiguedad: parseInt(e.target.value) || 0})} placeholder="0" className="text-right" data-testid="form-hasta-anios" />
              </div>
              <div className="space-y-2">
                <Label>% de Depreciación</Label>
                <Input type="number" step="0.01" value={formData.porcentaje_depreciacion || ""} onChange={(e) => setFormData({...formData, porcentaje_depreciacion: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" data-testid="form-porcentaje" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablaDepreciacion;
